// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  pages/Configuracoes/DashboardPerfis.tsx
//  Dashboards PADRÃO: da empresa (todos os usuários) e por perfil de acesso.
//  Protegida pelo formulário 254 (DashboardConfig). Cascata de resolução no
//  dashboard de cada usuário: personalização → perfil → empresa → template.
// ════════════════════════════════════════════════════════════════════════════

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Check, LayoutDashboard, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { permissoesAdminService } from "@/features/permissoes/services/permissoesAdminService";
import { dashboardService } from "@/features/dashboard/services/dashboardService";
import { DashboardEditor } from "@/features/dashboard/components/DashboardEditor";
import type { WidgetPos } from "@/features/dashboard/types";

// Sentinela do Select (nunca usar value="" em Radix Select).
const ALVO_EMPRESA = "__empresa__";

const DashboardPerfis = () => {
  const qc = useQueryClient();
  /** ALVO_EMPRESA = padrão da empresa; string numérica = perfilId. */
  const [alvo, setAlvo] = useState<string | null>(null);
  const [rascunho, setRascunho] = useState<WidgetPos[]>([]);
  const [tinhaLayout, setTinhaLayout] = useState(false);

  const ehEmpresa = alvo === ALVO_EMPRESA;
  const perfilId = alvo !== null && !ehEmpresa ? Number(alvo) : null;

  const { data: perfis = [], isLoading: carregandoPerfis } = useQuery({
    queryKey: ["perfis-combo"],
    queryFn: permissoesAdminService.listarPerfis,
    staleTime: 5 * 60_000,
  });

  const { isLoading: carregandoLayout } = useQuery({
    queryKey: ["dashboard-layout-admin", alvo],
    queryFn: async () => {
      const resolvido = ehEmpresa
        ? await dashboardService.getLayoutEmpresa()
        : await dashboardService.getLayoutPerfil(perfilId!);
      setRascunho(resolvido.layout?.widgets ?? []);
      setTinhaLayout(resolvido.layout !== null);
      return resolvido;
    },
    enabled: alvo !== null,
  });

  const salvar = useMutation({
    mutationFn: () =>
      ehEmpresa
        ? dashboardService.salvarLayoutEmpresa({ versao: 1, widgets: rascunho })
        : dashboardService.salvarLayoutPerfil(perfilId!, { versao: 1, widgets: rascunho }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["dashboard-layout-admin", alvo] });
      setTinhaLayout(true);
      toast.success(ehEmpresa ? "Layout padrão da empresa salvo." : "Layout do perfil salvo.");
    },
    onError: () => toast.error("Não foi possível salvar o layout."),
  });

  const remover = useMutation({
    mutationFn: () =>
      ehEmpresa
        ? dashboardService.removerLayoutEmpresa()
        : dashboardService.removerLayoutPerfil(perfilId!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["dashboard-layout-admin", alvo] });
      setRascunho([]);
      setTinhaLayout(false);
      toast.success("Layout removido.");
    },
    onError: () => toast.error("Não foi possível remover o layout."),
  });

  return (
    <div className="p-6 space-y-4 animate-fade-in">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold font-heading text-foreground tracking-tight">
            <LayoutDashboard className="h-6 w-6 text-primary" />
            Dashboards Padrão
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Defina o dashboard padrão da empresa e de cada perfil. Resolução por usuário:
            personalização própria → perfil → empresa → padrão do sistema.
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <Select value={alvo ?? undefined} onValueChange={setAlvo}>
            <SelectTrigger className="w-64">
              <SelectValue placeholder={carregandoPerfis ? "Carregando..." : "Selecione empresa ou perfil"} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALVO_EMPRESA}>Padrão da empresa (todos)</SelectItem>
              {perfis.map((p) => (
                <SelectItem key={p.id} value={String(p.id)}>Perfil: {p.nome}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {alvo !== null && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => remover.mutate()}
                disabled={!tinhaLayout || remover.isPending}
              >
                <Trash2 className="h-4 w-4 mr-1" /> Remover layout
              </Button>
              <Button size="sm" onClick={() => salvar.mutate()} disabled={salvar.isPending}>
                <Check className="h-4 w-4 mr-1" /> Salvar
              </Button>
            </>
          )}
        </div>
      </div>

      {alvo === null ? (
        <div className="py-16 text-center text-sm text-muted-foreground border border-dashed border-border/60 rounded-lg">
          Selecione o padrão da empresa ou um perfil para montar o dashboard.
        </div>
      ) : carregandoLayout ? (
        <div className="space-y-4">
          <Skeleton className="h-28 w-full rounded-lg" />
          <Skeleton className="h-64 w-full rounded-lg" />
        </div>
      ) : (
        <>
          {!tinhaLayout && rascunho.length === 0 && (
            <p className="text-xs text-muted-foreground">
              {ehEmpresa
                ? "A empresa ainda não tem layout padrão — os usuários veem o padrão do sistema. Monte abaixo e salve, ou comece de um modelo do segmento."
                : "Este perfil ainda não tem layout definido. Monte abaixo e salve, ou comece de um modelo."}
            </p>
          )}
          {/* Admin monta o dashboard para TERCEIROS: catálogo completo, sem
              filtrar pelas permissões do admin. Cada usuário final só verá os
              widgets que o SEU perfil permite (filtro na renderização). */}
          <DashboardEditor widgets={rascunho} onChange={setRascunho} filtrarPorPermissao={false} />
        </>
      )}
    </div>
  );
};

export default DashboardPerfis;
